/**
 * Organizations — clients, and everything else (phase 2 §3.1).
 *
 * §3.1 used to say creating one atomically creates its space. That is now true
 * of a **client** and only a client: a prospect, vendor, or partner has no
 * content space at all, because minting one per pipeline entry would make the
 * space tree the prospect list (§3.3).
 *
 * The result panel after creating a client still shows both rows side by side,
 * plus the creator grants written on the new space. That is the point of
 * rendering it: atomicity is invisible when the screen only confirms "created",
 * and the pairing is the thing a person needs to have seen once to trust it
 * afterwards. For a prospect it shows what was deliberately *not* made.
 */

import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { Form } from 'react-aria-components';
import { useCreateOrganization, useOrganizations } from '../../api/queries.ts';
import type { CreateOrganizationResponse, OrgRelationshipType } from '../../api/types.ts';
import { formatDate } from '../../components/format.ts';
import { RELATIONSHIP_OPTIONS, RelationshipBadges } from '../../components/relationships.tsx';
import { Badge } from '../../components/ui/badge.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Dialog, DialogBody, DialogFooter } from '../../components/ui/dialog.tsx';
import { Alert, ErrorState } from '../../components/ui/feedback.tsx';
import { Select, TextField } from '../../components/ui/field.tsx';
import { Card, Mono, PageHeader } from '../../components/ui/layout.tsx';
import { Table, TableEmpty, TBody, TD, TH, THead, TR } from '../../components/ui/table.tsx';

function CreateOrganizationForm({ onClose }: { onClose: () => void }) {
  const create = useCreateOrganization();
  const organizations = useOrganizations();
  const [name, setName] = useState('');
  const [spaceName, setSpaceName] = useState('');
  const [parentOrgId, setParentOrgId] = useState<string>('');
  const [relationship, setRelationship] = useState<OrgRelationshipType>('prospect');
  const [result, setResult] = useState<CreateOrganizationResponse | null>(null);

  const parentOptions = [
    { value: '', label: 'None — a top-level organization' },
    ...(organizations.data?.organizations ?? []).map((org) => ({
      value: org.id,
      label: org.name,
    })),
  ];

  if (result) {
    return (
      <>
        <DialogBody>
          {result.space ? (
            <>
              <Alert tone="success" title="One transaction, three rows">
                A client organization, its space, and the shared space beneath it. None of them
                could exist without the others.
              </Alert>
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-lg border border-secondary p-3">
                  <p className="text-xs font-semibold text-tertiary uppercase">Organization</p>
                  <p className="mt-1 text-sm font-medium text-primary">
                    {result.organization.name}
                  </p>
                  <Mono>{result.organization.id}</Mono>
                </div>
                <div className="rounded-lg border border-secondary p-3">
                  <p className="text-xs font-semibold text-tertiary uppercase">Its space</p>
                  <p className="mt-1 text-sm font-medium text-primary">{result.space.name}</p>
                  <Mono>{result.space.id}</Mono>
                  <p className="mt-1">
                    <Badge tone="outline">{result.space.kind}</Badge>
                  </p>
                </div>
                <div className="rounded-lg border border-secondary p-3">
                  <p className="text-xs font-semibold text-tertiary uppercase">Portal home</p>
                  <p className="mt-1 text-sm font-medium text-primary">
                    {result.sharedSpace?.name}
                  </p>
                  <Mono>{result.sharedSpace?.id ?? ''}</Mono>
                  <p className="mt-1">
                    <Badge tone="outline">{result.sharedSpace?.kind}</Badge>
                  </p>
                </div>
              </div>
              <div>
                <p className="text-sm text-tertiary">
                  {result.creatorGrants.length} grant(s) were written on the new space so you can
                  reach it and widen access from there. A brand-new space reaches nobody otherwise —
                  §4.6 rule 4 answers "no grant anywhere" with denied, including for the person who
                  just made it.
                </p>
                <ul className="mt-2 flex flex-wrap gap-2">
                  {result.creatorGrants.map((grant) => (
                    <li key={grant.id}>
                      <Badge tone="outline" mono>
                        {grant.capability}
                      </Badge>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          ) : (
            <>
              <Alert tone="success" title="One row, and deliberately no space">
                {result.organization.name} exists as a record. Nothing was minted in the space tree,
                because a pipeline entry does not need a container with an ACL — that arrives if and
                when they become a client.
              </Alert>
              <p className="text-sm text-tertiary">
                Until then, everyone holding <span className="font-mono">org.manage</span> can see
                and edit this record. Per-record access control begins at conversion.
              </p>
            </>
          )}
        </DialogBody>
        <DialogFooter>
          {result.space ? (
            <Link
              to="/spaces/$spaceId"
              params={{ spaceId: result.space.id }}
              className="no-underline"
            >
              <Button variant="secondary">Open the space</Button>
            </Link>
          ) : null}
          <Link
            to="/organizations/$organizationId"
            params={{ organizationId: result.organization.id }}
            className="no-underline"
          >
            <Button variant="primary">Open the organization</Button>
          </Link>
        </DialogFooter>
      </>
    );
  }

  return (
    <Form
      onSubmit={(event) => {
        event.preventDefault();
        create.mutate(
          {
            name: name.trim(),
            relationship,
            ...(spaceName.trim() ? { spaceName: spaceName.trim() } : {}),
            ...(parentOrgId ? { parentOrgId } : {}),
          },
          { onSuccess: setResult },
        );
      }}
    >
      <DialogBody>
        <TextField label="Organization name" value={name} onChange={setName} isRequired autoFocus />
        <Select
          label="Relationship"
          options={RELATIONSHIP_OPTIONS}
          selectedKey={relationship}
          onSelectionChange={(value) => setRelationship(value as OrgRelationshipType)}
          description="Only a client gets a space. The others are records, and can be converted later."
        />
        {relationship === 'client' ? (
          <TextField
            label="Space name"
            value={spaceName}
            onChange={setSpaceName}
            placeholder={name || "Defaults to the organization's name"}
            description="The pair is created in the same transaction, at the root of the space tree."
          />
        ) : null}
        <Select
          label="Parent organization"
          options={parentOptions}
          selectedKey={parentOrgId}
          onSelectionChange={setParentOrgId}
          description="Grouping for reporting only. A subsidiary is a sibling in the space tree, so grants on the parent never reach it."
        />
        {create.isError ? <ErrorState error={create.error} /> : null}
      </DialogBody>
      <DialogFooter>
        <Button variant="tertiary" onPress={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          isPending={create.isPending}
          isDisabled={name.trim() === ''}
        >
          {relationship === 'client' ? 'Create organization and spaces' : 'Create organization'}
        </Button>
      </DialogFooter>
    </Form>
  );
}

export function OrganizationListPage() {
  const organizations = useOrganizations();
  const [creating, setCreating] = useState(false);

  const byId = new Map((organizations.data?.organizations ?? []).map((org) => [org.id, org]));

  return (
    <>
      <PageHeader
        title="Organizations"
        description="Clients, prospects, vendors, and partners — one table, read by what each one is to Matchbox. Only a client has a content space; the rest are records, and converting one mints the pair."
        actions={
          <Button variant="primary" onPress={() => setCreating(true)}>
            Create organization
          </Button>
        }
      />

      <Card padded={false}>
        {organizations.isError ? (
          <div className="p-5">
            <ErrorState error={organizations.error} />
          </div>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Organization</TH>
                <TH>Relationship</TH>
                <TH>Paired space</TH>
                <TH>Parent</TH>
                <TH>Status</TH>
                <TH>Created</TH>
              </TR>
            </THead>
            <TBody>
              {organizations.isPending ? (
                <TableEmpty colSpan={6}>Loading…</TableEmpty>
              ) : organizations.data.organizations.length === 0 ? (
                <TableEmpty colSpan={6}>
                  No organizations you can reach. The list is a union of two rules: organizations
                  whose space resolves to allow for you, and spaceless ones if your role carries
                  org.manage.
                </TableEmpty>
              ) : (
                organizations.data.organizations.map((org) => (
                  <TR key={org.id} className="hover:bg-secondary">
                    <TD>
                      <Link
                        to="/organizations/$organizationId"
                        params={{ organizationId: org.id }}
                        className="font-medium text-primary no-underline hover:underline"
                      >
                        {org.name}
                      </Link>
                    </TD>
                    <TD>
                      <RelationshipBadges relationships={org.relationships} />
                    </TD>
                    <TD>
                      {org.space ? (
                        <>
                          <Link
                            to="/spaces/$spaceId"
                            params={{ spaceId: org.space.id }}
                            className="link"
                          >
                            {org.space.name}
                          </Link>
                          <Badge tone="outline" className="ml-2">
                            {org.space.kind}
                          </Badge>
                        </>
                      ) : (
                        <span className="text-sm text-tertiary">None — not a client</span>
                      )}
                    </TD>
                    <TD>
                      {org.parentOrgId ? (
                        <Link
                          to="/organizations/$organizationId"
                          params={{ organizationId: org.parentOrgId }}
                          className="text-sm text-tertiary no-underline"
                        >
                          {byId.get(org.parentOrgId)?.name ?? org.parentOrgId}
                        </Link>
                      ) : (
                        <span className="text-tertiary">—</span>
                      )}
                    </TD>
                    <TD>
                      <Badge tone={org.status === 'active' ? 'success' : 'gray'}>
                        {org.status}
                      </Badge>
                    </TD>
                    <TD className="whitespace-nowrap">{formatDate(org.createdAt)}</TD>
                  </TR>
                ))
              )}
            </TBody>
          </Table>
        )}
      </Card>

      <Dialog
        title="Create an organization"
        description="A client's organization and its spaces are written together, or not at all. Everything else is one row."
        isOpen={creating}
        onOpenChange={setCreating}
        size="lg"
      >
        {(close) => <CreateOrganizationForm onClose={close} />}
      </Dialog>
    </>
  );
}
